import { Injectable } from '@angular/core';
import { ApiService } from 'app/shared/apis/api.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { DriverTripState, DriverTripView, DriverCompletedView } from '../types/driver-trip.types';

@Injectable({ providedIn: 'root' })
export class DriverTripService {
    private readonly tripRefreshSubject = new BehaviorSubject<number | null>(null);

    readonly tripRefresh$ = this.tripRefreshSubject.asObservable();

    constructor(private readonly api: ApiService) {}

    getPendingAssignment(): Observable<DriverTripState | null> {
        return this.api.getSub<DriverTripState | null>('driver/assignments/pending');
    }

    getTrip(serviceId: number): Observable<DriverTripState> {
        return this.api.getSub<DriverTripState>(`driver/trip/${serviceId}`);
    }

    accept(serviceId: number): Observable<DriverTripState> {
        return this.api.postSub<DriverTripState>(`driver/assignments/${serviceId}/accept`, {});
    }

    decline(serviceId: number): Observable<{ success: boolean }> {
        return this.api.postSub<{ success: boolean }>(`driver/assignments/${serviceId}/decline`, {});
    }

    advanceStep(serviceId: number, stepKey: string): Observable<DriverTripState> {
        return this.api.patchSub<DriverTripState>(`driver/trip/${serviceId}/step`, { stepKey });
    }

    notifyTripRefresh(serviceId: number | null): void {
        this.tripRefreshSubject.next(serviceId);
    }

    mapToView(trip: DriverTripState): DriverTripView {
        const distanceLabel =
            trip.distance_km != null && trip.eta_minutes != null
                ? `~${trip.distance_km} km · ${trip.eta_minutes} min`
                : trip.distance_km != null
                  ? `~${trip.distance_km} km`
                  : trip.eta_minutes != null
                    ? `${trip.eta_minutes} min`
                    : '—';

        return {
            patientName: trip.patient_name,
            patientInitial: trip.patient_initial,
            mobility: trip.mobility,
            pickup: trip.pickup,
            destination: trip.destination,
            pickupTime: trip.pickup_time,
            routeShort: trip.route_short,
            distanceLabel,
            steps: trip.steps,
            currentStepKey: trip.current_step_key,
            isPending: trip.is_pending,
            isScheduledAccepted: trip.is_scheduled_accepted,
            requireDriverPhoto: !!trip.require_driver_photo_after_service,
            hasDriverProofPhoto: !!trip.has_driver_proof_photo,
            awaitingDriverProof: !!trip.awaiting_driver_proof,
        };
    }

    mapToCompletedView(trip: DriverTripState): DriverCompletedView {
        const awaitingCentralClose = !!trip.awaiting_central_close && !trip.service_ended;
        return {
            title: awaitingCentralClose ? 'Retorno registado' : 'Atendimento concluído',
            subtitle: trip.completed_subtitle ?? trip.patient_name,
            times: trip.completed_times ?? [],
            awaitingCentralClose,
        };
    }
}
